import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import SpeechToTextButton from '../ui/speech-to-text';
import { 
    Scan, Clock, Moon, Activity, FileImage, AlertTriangle, CheckCircle, Stethoscope, Mic, Sparkles, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Condition-specific clinical examination findings
const CONDITION_CLINICAL_EXAMS = {
    // Carpal Tunnel Syndrome
    carpal_tunnel_syndrome: {
        title: "Carpal Tunnel Syndrome Clinical Signs",
        description: "Positive findings support referral approval",
        autoApprove: true, // Any positive = auto-approve
        findings: [
            { id: 'thenar_wasting', label: 'Thenar wasting' },
            { id: 'motor_weakness', label: 'Motor weakness (APB/opponens)' },
            { id: 'positive_tinel_phalen', label: "Positive Tinel's / Phalen's test" },
            { id: 'median_sensory_loss', label: 'Median nerve sensory loss' },
            { id: 'night_pain_despite_splint', label: 'Persistent night pain despite splintage' },
            { id: 'emg_positive', label: 'EMG/Nerve conduction studies positive' }
        ]
    },
    // Ulnar Nerve Compression
    ulnar_nerve_compression: {
        title: "Ulnar Nerve Compression Clinical Signs",
        description: "Document findings despite conservative treatment",
        autoApprove: true,
        findings: [
            { id: 'persistent_paraesthesia', label: 'Persistent paraesthesia/numbness despite splint, physio & lifestyle modification' },
            { id: 'ulnar_sensory_loss', label: 'Ulnar nerve sensory loss (little finger, ring finger)' },
            { id: 'intrinsic_weakness', label: 'Intrinsic muscle weakness (interossei)' },
            { id: 'clawing', label: 'Clawing of ring/little fingers' },
            { id: 'positive_elbow_flexion', label: 'Positive elbow flexion test' },
            { id: 'froment_sign', label: "Positive Froment's sign" }
        ]
    },
    // Spine conditions - neurological signs
    spinal_stenosis: {
        title: "Focal Neurological Signs & Claudication",
        description: "Positive neurological findings or spinal claudication indicate referral eligibility",
        autoApprove: true,
        findings: [
            { id: 'neurogenic_claudication', label: 'Neurogenic/Spinal claudication (leg symptoms with walking)' },
            { id: 'spinal_claudication', label: 'Confirmed spinal claudication pattern' },
            { id: 'motor_weakness_mrc', label: 'Objective motor weakness (MRC grade <4)' },
            { id: 'foot_drop', label: 'Foot drop' },
            { id: 'sensory_loss', label: 'Objective sensory loss/deficit' },
            { id: 'dermatomal_numbness', label: 'Dermatomal distribution numbness' },
            { id: 'positive_straight_leg', label: 'Positive straight leg raise' },
            { id: 'reduced_reflexes', label: 'Reduced/absent reflexes' },
            { id: 'radicular_pain', label: 'Radicular leg pain (sciatica pattern)' }
        ]
    },
    disc_herniation: {
        title: "Focal Neurological Signs & Radiculopathy",
        description: "Neurological deficit WITH radicular symptoms indicates urgent referral. Radicular pain alone without deficit may be managed conservatively.",
        autoApprove: true,
        findings: [
            { id: 'motor_weakness_mrc', label: 'Objective motor weakness (MRC grade <4)' },
            { id: 'foot_drop', label: 'Foot drop' },
            { id: 'sensory_loss', label: 'Objective sensory loss/deficit' },
            { id: 'dermatomal_numbness', label: 'Dermatomal distribution numbness' },
            { id: 'positive_straight_leg', label: 'Positive straight leg raise' },
            { id: 'reduced_reflexes', label: 'Reduced/absent reflexes' },
            { id: 'radicular_pain', label: 'Radicular leg pain (sciatica pattern)' },
            { id: 'neurogenic_claudication', label: 'Neurogenic claudication' }
        ]
    },
    spondylolisthesis: {
        title: "Focal Neurological Signs & Claudication",
        description: "Positive neurological findings or claudication indicate urgent referral",
        autoApprove: true,
        findings: [
            { id: 'neurogenic_claudication', label: 'Neurogenic/Spinal claudication (leg symptoms with walking)' },
            { id: 'spinal_claudication', label: 'Confirmed spinal claudication pattern' },
            { id: 'motor_weakness_mrc', label: 'Objective motor weakness (MRC grade <4)' },
            { id: 'foot_drop', label: 'Foot drop' },
            { id: 'sensory_loss', label: 'Objective sensory loss/deficit' },
            { id: 'dermatomal_numbness', label: 'Dermatomal distribution numbness' },
            { id: 'radicular_pain', label: 'Radicular leg pain (sciatica pattern)' },
            { id: 'reduced_reflexes', label: 'Reduced/absent reflexes' }
        ]
    },
    cervical_spondylosis: {
        title: "Cervical Myelopathy Signs",
        description: "Myelopathic signs require urgent referral",
        autoApprove: true,
        findings: [
            { id: 'motor_weakness_mrc', label: 'Objective motor weakness (MRC grade <4)' },
            { id: 'loss_motor_skills', label: 'Loss of fine motor skills / hand clumsiness' },
            { id: 'ataxia', label: 'Ataxia / gait disturbance' },
            { id: 'hyperreflexia', label: 'Hyperreflexia' },
            { id: 'hoffmann_sign', label: "Positive Hoffmann's sign" },
            { id: 'lhermitte_sign', label: "Positive Lhermitte's sign" },
            { id: 'dermatomal_numbness', label: 'Dermatomal distribution numbness' },
            { id: 'bladder_bowel', label: 'Bladder/bowel dysfunction' }
        ]
    },
    // Shoulder instability
    shoulder_instability: {
        title: "Instability Clinical Signs",
        description: "Document instability findings",
        autoApprove: false,
        findings: [
            { id: 'positive_apprehension', label: 'Positive apprehension test' },
            { id: 'positive_relocation', label: 'Positive relocation test' },
            { id: 'sulcus_sign', label: 'Positive sulcus sign' },
            { id: 'load_shift', label: 'Abnormal load and shift' },
            { id: 'recurrent_dislocations', label: 'History of recurrent dislocations' }
        ]
    },
    // ACL / Knee ligament
    acl_rupture: {
        title: "Ligament Instability Signs",
        description: "Document clinical instability",
        autoApprove: false,
        findings: [
            { id: 'positive_lachman', label: "Positive Lachman's test" },
            { id: 'positive_anterior_drawer', label: 'Positive anterior drawer' },
            { id: 'positive_pivot_shift', label: 'Positive pivot shift' },
            { id: 'functional_instability', label: 'Functional giving way' },
            { id: 'mri_confirmed', label: 'MRI confirmed ACL tear' }
        ]
    },
    patellar_instability: {
        title: "Patellar Instability Signs",
        description: "Document patellar tracking issues",
        autoApprove: false,
        findings: [
            { id: 'positive_apprehension', label: 'Positive patellar apprehension' },
            { id: 'j_sign', label: 'J-sign present' },
            { id: 'lateral_tracking', label: 'Lateral patellar tracking' },
            { id: 'recurrent_subluxation', label: 'History of recurrent subluxation/dislocation' },
            { id: 'trochlear_dysplasia', label: 'Trochlear dysplasia on imaging' }
        ]
    }
};

// Spine condition check - all spine conditions use same neurological signs
const SPINE_CONDITIONS = ['spinal_stenosis', 'disc_herniation', 'spondylolisthesis', 'cervical_spondylosis'];

export default function ClinicalFindingsStep() {
    const { 
        clinical_findings, 
        updateClinicalFindings, 
        updateNotes, 
        clinical_notes, 
        urgency_justification,
        condition,
        body_part,
        patient,
        treatment_history,
        questionnaire_scores
    } = useReferral();

    const [generatingSummary, setGeneratingSummary] = useState(false);

    // Get condition-specific clinical exams
    const conditionExams = useMemo(() => {
        // Check if this condition has specific clinical exams
        if (CONDITION_CLINICAL_EXAMS[condition]) {
            return CONDITION_CLINICAL_EXAMS[condition];
        }
        // For spine conditions not explicitly listed, use spinal_stenosis template
        if (body_part === 'spine') {
            return CONDITION_CLINICAL_EXAMS.spinal_stenosis;
        }
        return null;
    }, [condition, body_part]);

    const handleChange = (field, value) => {
        updateClinicalFindings({ [field]: value });
    };

    const handleClinicalExamChange = (examId, checked) => {
        const currentExams = clinical_findings.condition_specific_exams || {};
        updateClinicalFindings({
            condition_specific_exams: {
                ...currentExams,
                [examId]: checked
            }
        });
    };

    // Generate AI clinical summary
    const generateAISummary = async () => {
        setGeneratingSummary(true);
        try {
            const referralContext = {
                patient_name: `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim(),
                patient_age: patient?.date_of_birth ? calculateAge(patient.date_of_birth) : null,
                body_part,
                condition,
                clinical_findings: {
                    affected_side: clinical_findings.affected_side,
                    symptom_duration_months: clinical_findings.symptom_duration_months,
                    pain_at_rest: clinical_findings.pain_at_rest,
                    night_pain: clinical_findings.night_pain,
                    functional_limitation: clinical_findings.functional_limitation,
                    examination_findings: clinical_findings.examination_findings,
                    xray_findings: clinical_findings.xray_findings,
                    mri_findings: clinical_findings.mri_findings,
                    condition_specific_exams: clinical_findings.condition_specific_exams
                },
                treatment_history: {
                    physiotherapy: treatment_history?.physiotherapy,
                    physio_sessions: treatment_history?.physio_sessions,
                    regular_analgesia: treatment_history?.regular_analgesia,
                    analgesia_type: treatment_history?.analgesia_type,
                    intra_articular_injections: treatment_history?.intra_articular_injections
                },
                questionnaire_scores: {
                    vas_pain_score: questionnaire_scores?.vas_pain_score,
                    oxford_hip_score: questionnaire_scores?.oxford_hip_score,
                    oxford_knee_score: questionnaire_scores?.oxford_knee_score,
                    spadi_score: questionnaire_scores?.spadi_score,
                    meps_score: questionnaire_scores?.meps_score,
                    cts6_score: questionnaire_scores?.cts6_score,
                    quickdash_score: questionnaire_scores?.quickdash_score
                }
            };

            const response = await axios.post(`${API}/ai/generate-summary`, { referral_context: referralContext });
            
            if (response.data.summary) {
                updateNotes({ clinical_notes: response.data.summary, urgency_justification });
                toast.success('Clinical summary generated! You can now edit it as needed.');
            }
        } catch (error) {
            console.error('Failed to generate summary:', error);
            toast.error('Failed to generate summary. Please try again or write manually.');
        } finally {
            setGeneratingSummary(false);
        }
    };

    // Helper function to calculate age
    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return null;
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age >= 0 ? age : null;
    };

    // Count positive findings
    const positiveFindings = useMemo(() => {
        if (!conditionExams) return 0;
        const exams = clinical_findings.condition_specific_exams || {};
        return conditionExams.findings.filter(f => exams[f.id]).length;
    }, [conditionExams, clinical_findings.condition_specific_exams]);

    // Check if auto-approve criteria met
    const autoApproveEligible = conditionExams?.autoApprove && positiveFindings > 0;

    return (
        <div className="space-y-6">
            {/* Condition-Specific Clinical Examinations */}
            {conditionExams && (
                <Card className={`card-clinical ${autoApproveEligible ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <Stethoscope className={`w-5 h-5 ${autoApproveEligible ? 'text-emerald-600' : 'text-amber-600'}`} />
                            {conditionExams.title}
                            <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                        </CardTitle>
                        <CardDescription className={autoApproveEligible ? 'text-emerald-700' : 'text-amber-700'}>
                            {conditionExams.description}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Status indicator */}
                        {conditionExams.autoApprove && (
                            <div className={`p-3 rounded-lg ${autoApproveEligible ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                                {autoApproveEligible ? (
                                    <p className="text-sm text-emerald-800 flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4" />
                                        <span><strong>{positiveFindings}</strong> positive finding(s) documented - referral criteria met</span>
                                    </p>
                                ) : (
                                    <p className="text-sm text-amber-800 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span>Please document clinical examination findings. Positive findings will support referral approval.</span>
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Clinical examination checkboxes */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {conditionExams.findings.map((finding) => (
                                <div
                                    key={finding.id}
                                    className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                        (clinical_findings.condition_specific_exams || {})[finding.id]
                                            ? 'border-emerald-400 bg-emerald-50'
                                            : 'border-slate-200 bg-white hover:border-slate-300'
                                    }`}
                                    onClick={() => handleClinicalExamChange(
                                        finding.id, 
                                        !(clinical_findings.condition_specific_exams || {})[finding.id]
                                    )}
                                >
                                    <Checkbox
                                        id={finding.id}
                                        checked={(clinical_findings.condition_specific_exams || {})[finding.id] || false}
                                        onCheckedChange={(checked) => handleClinicalExamChange(finding.id, checked)}
                                        data-testid={`exam-${finding.id}`}
                                    />
                                    <Label htmlFor={finding.id} className="font-medium text-slate-700 cursor-pointer text-sm">
                                        {finding.label}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Affected Side & Duration */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Activity className="w-5 h-5 text-teal-600" />
                        Clinical Assessment
                        <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="label-clinical">Affected Side (Laterality) *</Label>
                            <Select 
                                value={clinical_findings.affected_side} 
                                onValueChange={(value) => handleChange('affected_side', value)}
                            >
                                <SelectTrigger data-testid="affected-side-select" className="w-full">
                                    <SelectValue placeholder="Select side" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="left">Left</SelectItem>
                                    <SelectItem value="right">Right</SelectItem>
                                    <SelectItem value="bilateral">Bilateral</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label className="label-clinical">Symptom Duration (months) *</Label>
                            <Input
                                type="number"
                                min="0"
                                placeholder="12"
                                value={clinical_findings.symptom_duration_months || ''}
                                onChange={(e) => handleChange('symptom_duration_months', parseInt(e.target.value) || 0)}
                                className="input-clinical w-full"
                                data-testid="symptom-duration-input"
                            />
                        </div>
                    </div>

                    {/* Pain Characteristics */}
                    <div className="pt-4 border-t border-slate-100">
                        <h4 className="text-sm font-semibold text-slate-700 mb-4">Pain Characteristics</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 bg-white">
                                <Checkbox
                                    id="pain_at_rest"
                                    checked={clinical_findings.pain_at_rest}
                                    onCheckedChange={(checked) => handleChange('pain_at_rest', checked)}
                                    data-testid="pain-at-rest-checkbox"
                                />
                                <Label htmlFor="pain_at_rest" className="font-medium text-slate-700 cursor-pointer">
                                    Pain at rest
                                </Label>
                            </div>

                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 bg-white">
                                <Checkbox
                                    id="night_pain"
                                    checked={clinical_findings.night_pain}
                                    onCheckedChange={(checked) => handleChange('night_pain', checked)}
                                    data-testid="night-pain-checkbox"
                                />
                                <Label htmlFor="night_pain" className="font-medium text-slate-700 cursor-pointer flex items-center gap-2">
                                    <Moon className="w-4 h-4 text-slate-400" />
                                    Night pain
                                </Label>
                            </div>

                            <div className="space-y-2">
                                <Label className="label-clinical">Functional Limitation *</Label>
                                <Select 
                                    value={clinical_findings.functional_limitation} 
                                    onValueChange={(value) => handleChange('functional_limitation', value)}
                                >
                                    <SelectTrigger data-testid="functional-limitation-select">
                                        <SelectValue placeholder="Select severity" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="mild">Mild</SelectItem>
                                        <SelectItem value="moderate">Moderate</SelectItem>
                                        <SelectItem value="severe">Severe</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Examination Findings - MANDATORY */}
            <Card className="card-clinical border-rose-200">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Stethoscope className="w-5 h-5 text-teal-600" />
                        Examination Findings
                        <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                    </CardTitle>
                    <CardDescription className="text-rose-600">
                        Clinical examination findings must be documented before referral can be submitted
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex gap-2">
                        <Textarea
                            placeholder="Document key examination findings: range of motion, deformity, instability, special tests, neurovascular status, power (MRC grading), sensation, reflexes..."
                            value={clinical_findings.examination_findings}
                            onChange={(e) => handleChange('examination_findings', e.target.value)}
                            className={`input-clinical min-h-[120px] flex-1 ${
                                !clinical_findings.examination_findings ? 'border-rose-300' : ''
                            }`}
                            data-testid="examination-findings-textarea"
                        />
                        <SpeechToTextButton
                            onTranscription={(text) => {
                                const current = clinical_findings.examination_findings || '';
                                handleChange('examination_findings', current ? `${current} ${text}` : text);
                            }}
                            className="mt-1"
                        />
                    </div>
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                        <Mic className="w-3 h-3" />
                        Click the microphone button to dictate your findings
                    </p>
                    {!clinical_findings.examination_findings && (
                        <p className="text-xs text-rose-600 mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Examination findings are mandatory for referral submission
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Imaging */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <FileImage className="w-5 h-5 text-teal-600" />
                        Imaging Findings
                    </CardTitle>
                    <CardDescription>
                        Document relevant imaging findings and reports
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label className="label-clinical flex items-center gap-2">
                            <Scan className="w-4 h-4 text-slate-400" />
                            X-Ray Findings
                        </Label>
                        <div className="flex gap-2">
                            <Textarea
                                placeholder="e.g., Joint space narrowing, osteophytes, subchondral sclerosis, cysts..."
                                value={clinical_findings.xray_findings}
                                onChange={(e) => handleChange('xray_findings', e.target.value)}
                                className="input-clinical min-h-[80px] flex-1"
                                data-testid="xray-findings-textarea"
                            />
                            <SpeechToTextButton
                                onTranscription={(text) => {
                                    const current = clinical_findings.xray_findings || '';
                                    handleChange('xray_findings', current ? `${current} ${text}` : text);
                                }}
                                className="mt-1"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label className="label-clinical">MRI Findings (if performed)</Label>
                        <div className="flex gap-2">
                            <Textarea
                                placeholder="e.g., Cartilage loss, bone marrow edema, meniscal/ligament pathology..."
                                value={clinical_findings.mri_findings}
                                onChange={(e) => handleChange('mri_findings', e.target.value)}
                                className="input-clinical min-h-[80px] flex-1"
                                data-testid="mri-findings-textarea"
                            />
                            <SpeechToTextButton
                                onTranscription={(text) => {
                                    const current = clinical_findings.mri_findings || '';
                                    handleChange('mri_findings', current ? `${current} ${text}` : text);
                                }}
                                className="mt-1"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label className="label-clinical">Other Imaging/Investigations</Label>
                        <div className="flex gap-2">
                            <Textarea
                                placeholder="e.g., CT scan, ultrasound, nerve conduction studies, blood tests..."
                                value={clinical_findings.other_imaging}
                                onChange={(e) => handleChange('other_imaging', e.target.value)}
                                className="input-clinical min-h-[80px] flex-1"
                                data-testid="other-imaging-textarea"
                            />
                            <SpeechToTextButton
                                onTranscription={(text) => {
                                    const current = clinical_findings.other_imaging || '';
                                    handleChange('other_imaging', current ? `${current} ${text}` : text);
                                }}
                                className="mt-1"
                            />
                        </div>
                    </div>

                    {/* File Upload / URL Links for Investigation Documents */}
                    <div className="space-y-3 pt-4 border-t border-slate-100">
                        <Label className="label-clinical font-medium">Upload Investigation Files or Add Links</Label>
                        <p className="text-xs text-slate-500">
                            Attach relevant imaging files, reports, or provide links to PACS/cloud storage
                        </p>
                        
                        {/* File Upload */}
                        <div className="space-y-2">
                            <Label className="text-sm text-slate-600">Upload Files (max 20MB each)</Label>
                            <Input
                                type="file"
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png,.dcm,.doc,.docx"
                                onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    const currentFiles = clinical_findings.investigation_files || [];
                                    handleChange('investigation_files', [...currentFiles, ...files.map(f => ({
                                        name: f.name,
                                        size: f.size,
                                        type: f.type,
                                        file: f
                                    }))]);
                                }}
                                className="input-clinical"
                                data-testid="investigation-file-upload"
                            />
                            {clinical_findings.investigation_files?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {clinical_findings.investigation_files.map((file, idx) => (
                                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                                            {file.name}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newFiles = clinical_findings.investigation_files.filter((_, i) => i !== idx);
                                                    handleChange('investigation_files', newFiles);
                                                }}
                                                className="ml-1 hover:text-red-500"
                                            >
                                                ×
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* URL Links */}
                        <div className="space-y-2">
                            <Label className="text-sm text-slate-600">Add URL Links (PACS, cloud storage, etc.)</Label>
                            <div className="flex gap-2">
                                <Input
                                    type="url"
                                    placeholder="https://pacs.nhs.uk/study/12345 or cloud storage link"
                                    value={clinical_findings.new_link || ''}
                                    onChange={(e) => handleChange('new_link', e.target.value)}
                                    className="input-clinical flex-1"
                                    data-testid="investigation-url-input"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        if (clinical_findings.new_link?.trim()) {
                                            const currentLinks = clinical_findings.investigation_links || [];
                                            handleChange('investigation_links', [...currentLinks, clinical_findings.new_link.trim()]);
                                            handleChange('new_link', '');
                                        }
                                    }}
                                    data-testid="add-investigation-link"
                                >
                                    Add
                                </Button>
                            </div>
                            {clinical_findings.investigation_links?.length > 0 && (
                                <div className="space-y-1 mt-2">
                                    {clinical_findings.investigation_links.map((link, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm bg-slate-50 p-2 rounded">
                                            <a href={link} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline truncate flex-1">
                                                {link}
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newLinks = clinical_findings.investigation_links.filter((_, i) => i !== idx);
                                                    handleChange('investigation_links', newLinks);
                                                }}
                                                className="text-slate-400 hover:text-red-500"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Clinical Notes */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Additional Clinical Notes
                    </CardTitle>
                    <CardDescription>
                        Any additional information relevant to this referral
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label className="label-clinical">Clinical Summary</Label>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={generateAISummary}
                                disabled={generatingSummary}
                                className="flex items-center gap-2 text-teal-600 border-teal-300 hover:bg-teal-50"
                                data-testid="generate-summary-btn"
                            >
                                {generatingSummary ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4" />
                                        Generate AI Summary
                                    </>
                                )}
                            </Button>
                        </div>
                        <Textarea
                            placeholder="Summarise the clinical picture and reason for referral... Click 'Generate AI Summary' to auto-generate based on the form data, then edit as needed."
                            value={clinical_notes}
                            onChange={(e) => updateNotes({ clinical_notes: e.target.value, urgency_justification })}
                            className="input-clinical min-h-[120px]"
                            data-testid="clinical-notes-textarea"
                        />
                        <p className="text-xs text-slate-500">
                            Tip: Use AI to generate a summary, then review and edit as needed. The generated summary is fully editable.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label className="label-clinical">Urgency Justification (if requesting urgent review)</Label>
                        <Textarea
                            placeholder="If you believe this requires urgent review, please provide justification..."
                            value={urgency_justification}
                            onChange={(e) => updateNotes({ clinical_notes, urgency_justification: e.target.value })}
                            className="input-clinical min-h-[80px]"
                            data-testid="urgency-justification-textarea"
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
